import { getAuthModule } from '@littlebird/auth-client';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { getEnvVar } from '@/env';
import { playSound } from '@/features/sounds/sounds';
import { logError } from '@/lib/errorLogging';
import { queryClient } from '@/query/client';
import { userQueryOptions, type User } from '@/query/user';
import { userStore } from '@/stores/user.store';
import { listenWithAbortIPC } from '@/utils/abort';
import { setUserProperties, trackEvent } from '@/utils/analytics';
import { isDesktopMainWindow } from '@/utils/capabilities';
import { getDeepLinkProtocol } from '@/utils/platform';

import { electronApis } from '../electron-api';

import {
  extractNonAuthDeepLinkParams,
  getLoginBrowserAttributionFromSearchParams,
  type LoginBrowserAttribution,
} from './login-browser-attribution';
import { matchAttribution } from './social-login';
import { isSsoRequiredError } from './sso';

export function useBrowserLogin(options: {
  flow: 'onboarding' | 'login';
  afterSignIn: (user: User) => void | Promise<void>;
}) {
  const { flow, afterSignIn } = options;
  const [isOpeningBrowser, setIsOpeningBrowser] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const isLoggedIn = getAuthModule().tokenManager.hasValidToken();

  const handleAuthCallback = useCallback(
    async (
      accessToken: string,
      refreshToken: string,
      loginBrowserAttribution?: LoginBrowserAttribution
    ) => {
      const { is_new_user: isNewUser } =
        await backendApis.user.syncUser(accessToken);
      if (isNewUser) {
        matchAttribution(accessToken, loginBrowserAttribution);
      }
      await electronApis.auth.setAuthData({
        accessToken,
        refreshToken,
        clientId: getEnvVar('VITE_AUTH0_CLIENT_ID'),
        auth0Domain: getEnvVar('VITE_AUTH0_DOMAIN'),
        apiUrl: getEnvVar('VITE_API_URL'),
      });
      const user = await queryClient.ensureQueryData(
        userQueryOptions({ accessToken })
      );
      userStore.setUser(user);
      void playSound('hello');
      await afterSignIn(user);
    },
    [afterSignIn]
  );

  const processDeepLinkAuthCallback = useCallback(
    async (url: URL) => {
      const accessToken = url.searchParams.get('access_token');
      const refreshToken = url.searchParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        toast.error('Authentication failed: Missing authentication tokens');
        return;
      }

      setIsSigningIn(true);

      try {
        const nonAuthParams = extractNonAuthDeepLinkParams(url.searchParams);
        const loginBrowserAttribution =
          getLoginBrowserAttributionFromSearchParams(url.searchParams);
        if (Object.keys(nonAuthParams).length > 0) {
          setUserProperties(nonAuthParams);
        }
        await handleAuthCallback(
          accessToken,
          refreshToken,
          loginBrowserAttribution
        );
      } catch (error) {
        logError(error, 'Browser login callback failed', {
          context: 'browser-login-callback',
          metadata: {
            flow,
            hasAccessToken: Boolean(accessToken),
            hasRefreshToken: Boolean(refreshToken),
          },
        });

        setIsSigningIn(false);
        if (!isSsoRequiredError(error)) {
          toast.error('Failed to sign in. Please try again.');
        }
      }
    },
    [handleAuthCallback, flow]
  );

  useEffect(() => {
    if (!isDesktopMainWindow()) return;
    if (isLoggedIn) return;

    const abortController = new AbortController();

    listenWithAbortIPC(
      'deep-link',
      async (payload: { url: string }) => {
        const url = new URL(payload.url);
        if (url.host === 'auth-callback') {
          await processDeepLinkAuthCallback(url);
        }
      },
      abortController.signal
    );

    listenWithAbortIPC(
      'login-callback-rejected',
      (payload: { reason: string }) => {
        toast.error(
          payload.reason === 'expired'
            ? 'That sign-in link timed out — click Sign in to get a new one.'
            : "Couldn't complete sign-in. Click Sign in to try again."
        );
      },
      abortController.signal
    );

    return () => abortController.abort();
  }, [isLoggedIn, processDeepLinkAuthCallback]);

  const openBrowserLogin = useCallback(async () => {
    trackEvent('ACCEPTED_TERMS_AND_CONDITIONS', { accepted: true });
    setIsOpeningBrowser(true);

    try {
      const { id } = await electronApis.auth.startLoginAttempt();
      const desktopLoginUrl = `${getEnvVar('BASE_URL_FOR_PUBLIC_LINKS')}/desktop-login?state=${getDeepLinkProtocol()}&login_attempt=${id}`;
      electronApis.system.openExternal(desktopLoginUrl);
    } catch (error) {
      logError(error, 'Could not start browser login', {
        context: 'browser-login-start',
        metadata: { flow },
      });
      toast.error("Couldn't open the browser to sign in. Please try again.");
    }

    setTimeout(() => setIsOpeningBrowser(false), 2500);
  }, [flow]);

  const isBusy = isOpeningBrowser || isSigningIn;
  const signInButtonText = isSigningIn
    ? 'Signing you in...'
    : isOpeningBrowser
      ? 'Opening browser...'
      : 'Sign Up or Sign In';

  return {
    openBrowserLogin,
    isBusy,
    isLoggedIn,
    signInButtonText,
  };
}
